import { useEffect, useState } from 'react';
import { supabase } from '../utils/supabase';

export interface LeaderboardRow {
  id: string;
  userId: string | null;
  friendlyName: string;
  xp: number;
  rank: number | null;
  rarity: string | null;
  caps: number;
  attendanceStreak: number;
  maxAttendanceStreak: number;
  winRate: number | null;
  wins: number;
  draws: number;
  losses: number;
  totalGames: number;
  currentWinStreak: number;
  maxWinStreak: number;
  currentUnbeatenStreak: number;
  maxUnbeatenStreak: number;
  currentLossStreak: number;
  currentWinlessStreak: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifferential: number;
  bluePct: number | null;
  orangePct: number | null;
  /** In the WNF WhatsApp group ('Yes' or 'Proxy'). */
  whatsappMember: boolean;
}

export interface LeaderboardRowsResult {
  rows: LeaderboardRow[];
  loading: boolean;
  error: string | null;
}

export function useLeaderboardRows(enabled: boolean): LeaderboardRowsResult {
  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [
          playersRes, winRatesRes,
          winStreaksRes, unbeatenStreaksRes,
          lossStreaksRes, winlessStreaksRes,
          goalDiffRes, teamColorsRes,
        ] = await Promise.all([
          supabase
            .from('players')
            .select(`
              id, user_id, friendly_name, caps, current_streak, max_streak, whatsapp_group_member,
              player_xp ( xp, rank, rarity )
            `)
            .gt('caps', 0),
          supabase.rpc('get_player_win_rates', { target_year: null }),
          // Attendance streak comes from players.max_streak above.
          // player_streak_stats was queried here, but it counts every
          // registration row including reserves and dropouts, so its
          // longest_streak is inflated (Chris read 49 against a true 29).
          supabase.rpc('get_player_winning_streaks', { target_year: null }),
          supabase.rpc('get_player_unbeaten_streaks', { target_year: null }),
          supabase.rpc('get_player_losing_streaks', { target_year: null }),
          supabase.rpc('get_player_winless_streaks', { target_year: null }),
          supabase.rpc('get_player_goal_differentials', { target_year: null }),
          supabase.rpc('get_player_team_colors', { target_year: null }),
        ]);

        for (const r of [playersRes, winRatesRes,
          winStreaksRes, unbeatenStreaksRes,
          lossStreaksRes, winlessStreaksRes,
          goalDiffRes, teamColorsRes]) {
          if (r.error) throw r.error;
        }

        const winRateMap = new Map<string, { winRate: number | null; wins: number; draws: number; losses: number; totalGames: number }>();
        for (const w of (winRatesRes.data ?? []) as Array<{ id: string; win_rate: string | number | null; wins: number; draws: number; losses: number; total_games: number }>) {
          winRateMap.set(w.id, {
            winRate: w.win_rate != null ? Number(w.win_rate) : null,
            wins: w.wins ?? 0, draws: w.draws ?? 0, losses: w.losses ?? 0,
            totalGames: w.total_games ?? 0,
          });
        }

        const winStreakMap = new Map<string, { current: number; max: number }>();
        for (const s of (winStreaksRes.data ?? []) as Array<{ id: string; current_win_streak: number; max_win_streak: number }>) {
          winStreakMap.set(s.id, { current: s.current_win_streak ?? 0, max: s.max_win_streak ?? 0 });
        }

        const unbeatenStreakMap = new Map<string, { current: number; max: number }>();
        for (const s of (unbeatenStreaksRes.data ?? []) as Array<{ id: string; current_unbeaten_streak: number; max_unbeaten_streak: number }>) {
          unbeatenStreakMap.set(s.id, { current: s.current_unbeaten_streak ?? 0, max: s.max_unbeaten_streak ?? 0 });
        }

        const lossStreakMap = new Map<string, number>();
        for (const s of (lossStreaksRes.data ?? []) as Array<{ id: string; current_loss_streak: number }>) {
          lossStreakMap.set(s.id, s.current_loss_streak ?? 0);
        }

        const winlessStreakMap = new Map<string, number>();
        for (const s of (winlessStreaksRes.data ?? []) as Array<{ id: string; current_winless_streak: number }>) {
          winlessStreakMap.set(s.id, s.current_winless_streak ?? 0);
        }

        const goalDiffMap = new Map<string, { gf: number; ga: number; gd: number }>();
        for (const g of (goalDiffRes.data ?? []) as Array<{ id: string; goals_for: number; goals_against: number; goal_differential: number }>) {
          goalDiffMap.set(g.id, { gf: Number(g.goals_for) || 0, ga: Number(g.goals_against) || 0, gd: Number(g.goal_differential) || 0 });
        }

        const teamColorMap = new Map<string, { blue: number; orange: number }>();
        for (const t of (teamColorsRes.data ?? []) as Array<{ id: string; team: string; team_frequency: number }>) {
          const entry = teamColorMap.get(t.id) ?? { blue: 0, orange: 0 };
          if (t.team === 'blue') entry.blue = Number(t.team_frequency) * 100;
          if (t.team === 'orange') entry.orange = Number(t.team_frequency) * 100;
          teamColorMap.set(t.id, entry);
        }

        const combined: LeaderboardRow[] = (playersRes.data ?? []).map((p) => {
          const px = Array.isArray(p.player_xp) ? p.player_xp[0] : p.player_xp;
          const wr = winRateMap.get(p.id);
          const ws = winStreakMap.get(p.id);
          const us = unbeatenStreakMap.get(p.id);
          const gd = goalDiffMap.get(p.id);
          const tc = teamColorMap.get(p.id);
          return {
            id: p.id,
            userId: p.user_id ?? null,
            friendlyName: p.friendly_name,
            xp: px?.xp ?? 0,
            rank: px?.rank ?? null,
            rarity: px?.rarity ?? null,
            caps: p.caps ?? 0,
            attendanceStreak: p.current_streak ?? 0,
            maxAttendanceStreak: p.max_streak ?? p.current_streak ?? 0,
            winRate: wr?.winRate ?? null,
            wins: wr?.wins ?? 0,
            draws: wr?.draws ?? 0,
            losses: wr?.losses ?? 0,
            totalGames: wr?.totalGames ?? 0,
            currentWinStreak: ws?.current ?? 0,
            maxWinStreak: ws?.max ?? 0,
            currentUnbeatenStreak: us?.current ?? 0,
            maxUnbeatenStreak: us?.max ?? 0,
            currentLossStreak: lossStreakMap.get(p.id) ?? 0,
            currentWinlessStreak: winlessStreakMap.get(p.id) ?? 0,
            goalsFor: gd?.gf ?? 0,
            goalsAgainst: gd?.ga ?? 0,
            goalDifferential: gd?.gd ?? 0,
            bluePct: tc?.blue ?? null,
            orangePct: tc?.orange ?? null,
            whatsappMember: p.whatsapp_group_member === 'Yes' || p.whatsapp_group_member === 'Proxy',
          };
        });

        if (!cancelled) setRows(combined);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [enabled]);

  return { rows, loading, error };
}
